import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { SharedApiService, ApiType } from '../../../../../libs/shared/src/lib/services/api.service';
import { UserProfileExtended, LanguageExtended, SkillExtended, WorkExperienceExtended, ProjectExtended } from '../../../../../libs/shared/src/lib/models/dashboard/user-from-be';
import { UserProfileResponse } from '../models/user-profile.model';
import { AttachmentService } from '../../../../../libs/shared/src/lib/services/attachment.service';
import { formatDateForSubmission } from '../utils/date-utils';
import { Address } from 'libs/shared/src/lib/models/address.model';

@Injectable({
  providedIn: 'root',
})
export class UserProfileService {
  constructor(private sharedApiService: SharedApiService, private authService: AuthService, private attachmentService: AttachmentService) {}

  /**
   * Helper method to format education dates for API submission
   * @param education Education record with dates to format
   * @returns Education record with formatted dates
   */
  private formatEducationDates(education: any): any {
    const formattedEducation = { ...education };

    // Format startDate if it exists, return null if empty string
    if (formattedEducation.startDate) {
      formattedEducation.startDate = formatDateForSubmission(formattedEducation.startDate);
    } else if (formattedEducation.startDate === '') {
      formattedEducation.startDate = null;
    }

    // Format endDate if it exists and not currently studying, return null if empty string
    if (formattedEducation.endDate && !formattedEducation.isCurrentlyStudying && !formattedEducation.current) {
      formattedEducation.endDate = formatDateForSubmission(formattedEducation.endDate);
    } else if (formattedEducation.endDate === '') {
      formattedEducation.endDate = null;
    }

    return formattedEducation;
  }

  /**
   * Gets the user profile data for the currently logged in user
   * @returns Observable with the user profile data or null
   */
  getUserProfile(): Observable<UserProfileExtended | null> {
    // Check if user is logged in
    if (!this.authService.isLoggedIn()) {
      return throwError(() => new Error('User not logged in'));
    }

    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      return throwError(() => new Error('User data not available'));
    }

    // Use longcvID instead of id as requested
    const longcvID = currentUser['longcvID'];
    if (!longcvID) {
      return throwError(() => new Error('User does not have a CV ID'));
    }

    return this.sharedApiService.get<UserProfileResponse>(`cv?id=${longcvID}`).pipe(
      map((response) => {
        if (response.status !== 200) {
          throw new Error(`Error fetching user profile: ${response.message}`);
        }
        return response.body;
      }),
      catchError((error) => {
        console.error('Error fetching user profile:', error);
        return throwError(() => error);
      })
    );
  }

  // Removed getHeaders() method as we now use ApiService

  /**
   * Updates personal information (longCV) for the candidate
   * @param longCV Object containing personal information to update
   * @returns Observable with the API response
   */
  updatePersonalInfo(
    longCV: {
      id?: number;
      gender?: string;
      title?: string;
      titleAfterName?: string;
      birthDate?: string;
      telephone?: string;
      educationLevelID?: number;
      jobType?: string[];
      jobCategory?: string[];
      selfDescription?: string[];
      address?: Address
    },
    aiMatching: boolean
  ): Observable<any> {
    return this.sharedApiService.put<any>('api/candidate/update-personal-info', { longCV, aiMatching }, undefined, { apiType: ApiType.OPEN, jobsRequest: true }).pipe(
      map((response: any) => {
        // For 200 responses, consider it successful even if there's no specific status field
        // The response might be empty or have a different structure than expected
        return response; // Return whatever the API returns
      }),
      catchError((error) => {
        console.error('Error updating personal info:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Updates the languages array for the user profile
   * @param languages Array of Language objects to update
   * @returns Observable with the API response
   */
  updateLanguages(languages: LanguageExtended[]): Observable<any> {
    // Get the current user to get the longcvID
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      return throwError(() => new Error('User data not available'));
    }

    const longcvID = currentUser['longcvID'];
    if (!longcvID) {
      return throwError(() => new Error('User does not have a CV ID'));
    }

    // Prepare the payload with the languages array
    const payload = {
      languages: languages.map((lang) => ({
        personalDataID: longcvID,
        language: lang.language,
        level: lang.level,
        edit: false,
        isAiParsed: false,
      })),
      longCVId: longcvID,
    };

    // Make the API call to update languages
    return this.sharedApiService.put<any>('api/candidate/update-language', payload, undefined, { apiType: ApiType.OPEN, jobsRequest: true }).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((error) => {
        console.error('Error updating languages:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Updates the skills array for the user profile
   * @param skills Array of Skill objects to update
   * @returns Observable with the API response
   */
  updateSkills(skills: SkillExtended[]): Observable<any> {
    // Get the current user to get the longcvID
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      return throwError(() => new Error('User data not available'));
    }

    const longcvID = currentUser['longcvID'];
    if (!longcvID) {
      return throwError(() => new Error('User does not have a CV ID'));
    }

    // Prepare the payload with the skills array
    const payload = {
      skills: skills.map((skill) => ({
        personalDataID: longcvID,
        skillName: skill.skillName,
        level: skill.level,
        edit: false,
        isAiParsed: false,
      })),
      longCVId: longcvID,
    };

    // Make the API call to update skills
    return this.sharedApiService.put<any>('api/candidate/update-skills', payload, undefined, { apiType: ApiType.OPEN, jobsRequest: true }).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((error) => {
        console.error('Error updating skills:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Updates high school education information for the user profile
   * @param highSchoolEducations Array of high school education objects to update
   * @returns Observable with the API response
   */
  updateHighSchoolEducation(highSchoolEducations: any[]): Observable<any> {
    // Get the current user to get the longcvID
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      return throwError(() => new Error('User data not available'));
    }

    const longcvID = currentUser['longcvID'];
    if (!longcvID) {
      return throwError(() => new Error('User does not have a CV ID'));
    }

    // Prepare the payload with the high school educations array
    const payload = {
      highSchoolEducations: highSchoolEducations.map((education) => {
        // Format dates using the helper method
        const formattedEducation = this.formatEducationDates(education);

        // Base education object with formatted dates
        const educationObj = {
          id: education.id || null,
          highSchoolName: education.highSchoolName || education.schoolName,
          fieldOfStudy: education.fieldOfStudy,
          educationLevelID: education.educationLevelID || null,
          city: education.city,
          country: education.country,
          startDate: formattedEducation.startDate,
          endDate: formattedEducation.endDate,
          personalDataID: longcvID,
          edit: false,
        };

        // Only include attachment information if an attachment exists
        if (education.attachmentID) {
          return {
            ...educationObj,
            attachmentID: +education.attachmentID,
            candidateAttachment: {
              url: education.candidateAttachment?.url || null,
              fileType: education.candidateAttachment?.fileType || this.attachmentService.getFileTypeFromName(education.candidateAttachment?.fileName),
              type: 'EDUCATION_DOCUMENT',
              fileName: education.candidateAttachment?.fileName || '',
              fileSize: education.candidateAttachment?.fileSize || null,
            },
          };
        }

        return educationObj;
      }),
      longCVId: longcvID,
    };

    // Make the API call to update high school education
    return this.sharedApiService.put<any>('api/candidate/update-high-school', payload, undefined, { apiType: ApiType.OPEN, jobsRequest: true }).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((error) => {
        console.error('Error updating high school education:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Updates work experience information for the user profile
   * @param workExperiences Array of work experience objects to update
   * @returns Observable with the API response
   */
  updateWorkExperience(workExperiences: WorkExperienceExtended[]): Observable<any> {
    // Get the current user to get the longcvID
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      return throwError(() => new Error('User data not available'));
    }

    const longcvID = currentUser['longcvID'];
    if (!longcvID) {
      return throwError(() => new Error('User does not have a CV ID'));
    }

    // Prepare the payload with the work experiences array
    const payload = {
      experiences: workExperiences.map((experience) => {
        // Format dates using the helper method
        const formattedExperience = this.formatEducationDates(experience);

        // Base work experience object with formatted dates
        return {
          id: experience.id || null,
          employer: experience.employer || '',
          position: experience.position || '',
          city: experience.city || '',
          country: experience.country || '',
          description: experience.description || '', // Add description field
          startDate: formattedExperience.startDate,
          endDate: formattedExperience.endDate,
          current: experience.current || false,
          personalDataID: longcvID,
          edit: false,
        };
      }),
      longCVId: longcvID,
    };

    // Make the API call to update work experience
    return this.sharedApiService.put<any>('api/candidate/update-work-experience', payload, undefined, { apiType: ApiType.OPEN, jobsRequest: true }).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((error) => {
        console.error('Error updating work experience:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Updates projects information for the user profile
   * @param projects Array of project objects to update
   * @returns Observable with the API response
   */
  updateProjects(projects: ProjectExtended[]): Observable<any> {
    // Get the current user to get the longcvID
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      return throwError(() => new Error('User data not available'));
    }

    const longcvID = currentUser['longcvID'];
    if (!longcvID) {
      return throwError(() => new Error('User does not have a CV ID'));
    }

    // Prepare the payload with the projects array
    const payload = {
      projects: projects.map((project) => {
        // Base project object
        return {
          id: project.id || null,
          title: project.title || '',
          position: project.position || '',
          duration: project.duration !== undefined && project.duration !== null ? +project.duration : 0,
          country: project.country || '',
          personalDataID: longcvID,
          edit: false,
        };
      }),
      longCVId: longcvID,
    };

    // Make the API call to update projects
    return this.sharedApiService.put<any>('api/candidate/update-projects', payload, undefined, { apiType: ApiType.OPEN, jobsRequest: true }).pipe(
      map((response) => {
        return response;
      }),
      catchError((error) => {
        console.error('Error updating projects:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Updates university education information for the user profile
   * @param universityEducations Array of university education objects to update
   * @returns Observable with the API response
   */
  updateUniversityEducation(universityEducations: any[]): Observable<any> {
    // Get the current user to get the longcvID
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      return throwError(() => new Error('User data not available'));
    }

    const longcvID = currentUser['longcvID'];
    if (!longcvID) {
      return throwError(() => new Error('User does not have a CV ID'));
    }

    // Prepare the payload with the university educations array
    const payload = {
      universityEducations: universityEducations.map((education) => {
        // Format dates using the helper method
        const formattedEducation = this.formatEducationDates(education);

        // Base education object with formatted dates
        const educationObj = {
          id: education.id || null,
          universityName: education.universityName,
          fieldOfStudy: education.fieldOfStudy,
          educationLevelID: education.educationLevelID || null,
          city: education.city,
          country: education.country,
          startDate: formattedEducation.startDate,
          endDate: formattedEducation.endDate,
          current: education.isCurrentlyStudying || false,
          personalDataID: longcvID,
          edit: false,
        };

        // Only include attachment information if an attachment exists
        if (education.attachmentID) {
          return {
            ...educationObj,
            attachmentID: +education.attachmentID,
            candidateAttachment: {
              url: education.candidateAttachment?.url || null,
              fileType: education.candidateAttachment?.fileType || this.attachmentService.getFileTypeFromName(education.candidateAttachment?.fileName),
              type: 'EDUCATION_DOCUMENT',
              fileName: education.candidateAttachment?.fileName || '',
              fileSize: education.candidateAttachment?.fileSize || null,
            },
          };
        }

        return educationObj;
      }),
      longCVId: longcvID,
    };

    // Make the API call to update university education
    return this.sharedApiService.put<any>('api/candidate/update-university', payload, undefined, { apiType: ApiType.OPEN, jobsRequest: true }).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((error) => {
        console.error('Error updating university education:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Updates drivers licenses information for the user profile
   * @param driversLicenses JSON string array of drivers license types (e.g., '["AM","A1","B"]')
   * @returns Observable with the API response
   */
  updateDriversLicenses(driversLicenses: string): Observable<any> {
    // Get the current user to get the longcvID
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      return throwError(() => new Error('User data not available'));
    }

    const longcvID = currentUser['longcvID'];
    if (!longcvID) {
      return throwError(() => new Error('User does not have a CV ID'));
    }

    // Prepare the payload with the drivers licenses
    const payload = {
      longCVId: longcvID,
      driversLicenses: driversLicenses,
    };

    // Make the API call to update drivers licenses
    return this.sharedApiService.put<any>('api/candidate/update-drivers-licenses', payload, undefined, { apiType: ApiType.OPEN, jobsRequest: true }).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((error) => {
        console.error('Error updating drivers licenses:', error);
        return throwError(() => error);
      })
    );
  }
}
